import { useEffect, useState } from "react";

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

export default function ShareDialog({ open, onClose, title }: ShareDialogProps) {
  const [copied, setCopied] = useState(false);
  const [link, setLink] = useState("");

  useEffect(() => {
    if (open) {
      setLink(window.location.href);
      setCopied(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-primary-950/60 px-4 pb-6 pt-4 sm:items-center sm:pb-4">
      <button type="button" aria-label="关闭分享弹窗" onClick={onClose} className="absolute inset-0 cursor-default" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="分享视频"
        className="relative w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-base font-bold text-foreground-950">分享给朋友</h2>
            <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <input
            readOnly
            value={link}
            aria-label="视频链接"
            className="h-10 min-w-0 flex-1 truncate rounded-md border border-background-300 bg-background-100 px-3 text-xs text-foreground-700 outline-none"
          />
          <button
            type="button"
            onClick={copyLink}
            className={`flex h-10 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 text-xs font-semibold transition-colors ${
              copied ? "bg-primary-800 text-background-50" : "bg-accent-500 text-foreground-950 hover:bg-accent-400"
            }`}
          >
            <i className={copied ? "ri-check-line text-sm" : "ri-file-copy-line text-sm"} />
            {copied ? "已复制" : "复制链接"}
          </button>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          {[
            { icon: "ri-wechat-fill", label: "微信" },
            { icon: "ri-weibo-fill", label: "微博" },
            { icon: "ri-message-3-line", label: "短信" },
          ].map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={copyLink}
              className="flex cursor-pointer flex-col items-center gap-1.5 rounded-md border border-background-200 py-3 text-[11px] text-foreground-700 transition-colors hover:border-primary-200 hover:bg-primary-50/60"
            >
              <i className={`${item.icon} text-lg text-primary-700`} />
              {item.label}
            </button>
          ))}
        </div>

        <p className="mt-3 text-[11px] leading-relaxed text-foreground-500">
          演示站点：点击任意分享渠道都会复制当前页面链接，不会真实唤起第三方应用。
        </p>
      </div>
    </div>
  );
}